<ng-container *ngIf="canRead$ | async">
    <div *ngIf="viewModel$ | async as vm">

        <div class="p-d-flex p-ai-center p-mb-3">
            <h4 class="p-m-0">Unmatched payments</h4>

            <!-- The number this page exists for. It comes from its own query, so
                 the same value can be shown anywhere else without loading rows. -->
            <span *ngIf="vm.openCount" class="p-ml-2 p-tag p-tag-warning">
                {{ vm.openCount }} payment{{ vm.openCount === 1 ? '' : 's' }}
                need{{ vm.openCount === 1 ? 's' : '' }} review
            </span>

            <button
                type="button"
                pButton
                icon="pi pi-refresh"
                class="p-button-text p-ml-auto"
                pTooltip="Re-read the queue"
                [disabled]="vm.loading"
                (click)="store.dispatch(actions.refreshButtonClicked())"
            ></button>
        </div>

        <p class="p-mb-3 p-text-secondary">
            Payments taken on a terminal that could not be matched to a job. A sale whose job
            code matches exactly is filed automatically and never appears here.
        </p>

        <p-selectButton
            [options]="statusOptions"
            optionLabel="label"
            optionValue="value"
            [ngModel]="vm.statusFilter"
            (ngModelChange)="store.dispatch(actions.statusFilterChanged({ status: $event }))"
            class="p-mb-3 p-d-block"
        ></p-selectButton>

        <!-- Our own indicator, not the table's.
             PrimeNG's `[loading]` overlay is positioned over the table BODY, so
             with no rows to cover it collapses onto the header and reads as a
             spinner stuck in the column titles. Shown here it is unambiguous,
             and it appears in the same place whether the queue has fifty rows or
             none — which is exactly when someone is waiting to find out. -->
        <div *ngIf="vm.loading" class="p-d-flex p-ai-center p-mb-3 p-text-secondary">
            <i class="pi pi-spin pi-spinner p-mr-2"></i>
            <span>Reading the review queue…</span>
        </div>

        <p-table
            [value]="vm.page?.payments || []"
            [loading]="vm.loading"
            [showLoader]="false"
            dataKey="id"
            responsiveLayout="scroll"
        >
            <ng-template pTemplate="header">
                <tr>
                    <th>When</th>
                    <th>Customer paid</th>
                    <th>Credited to job</th>
                    <th>Job code keyed</th>
                    <th>Where</th>
                    <th>Why it is here</th>
                    <th></th>
                </tr>
            </ng-template>

            <ng-template pTemplate="body" let-payment>
                <tr>
                    <td>{{ when(payment.occurredAt) | date: 'short' }}</td>

                    <!-- Two amounts, always. They differ by the card differential on a
                         dual-pricing location, and the customer only ever knows the first. -->
                    <td>
                        <strong>{{ money(payment.amountPaid, payment.currency) }}</strong>
                        <div *ngIf="payment.differential" class="p-text-secondary p-text-sm">
                            includes {{ money(payment.differential, payment.currency) }} card differential
                        </div>
                        <!-- A tip is the customer's money but not the job's, so it is
                             named here and never folded into the credited amount. -->
                        <div *ngIf="payment.tip" class="p-text-secondary p-text-sm">
                            includes {{ money(payment.tip, payment.currency) }} tip
                        </div>
                    </td>
                    <td>{{ money(payment.baseAmount, payment.currency) }}</td>

                    <td>
                        <span *ngIf="payment.jobCode; else noCode">{{ payment.jobCode }}</span>
                        <ng-template #noCode>
                            <span class="p-text-secondary">none keyed</span>
                        </ng-template>
                    </td>

                    <td>
                        {{ terminal(payment.epi) }}
                        <div *ngIf="card(payment.cardBrand, payment.last4)" class="p-text-secondary p-text-sm">
                            {{ card(payment.cardBrand, payment.last4) }}
                        </div>
                    </td>

                    <td class="p-text-secondary">{{ payment.reason }}</td>

                    <td>
                        <button
                            *ngIf="payment.status === 'open' && (canResolve$ | async)"
                            type="button"
                            pButton
                            label="Resolve"
                            class="p-button-sm"
                            [disabled]="!!vm.resolvingId"
                            (click)="store.dispatch(actions.paymentSelected({
                                paymentId: vm.selectedId === payment.id ? undefined : payment.id
                            }))"
                        ></button>

                        <!-- Requirement: a resolved row says WHERE the money went.
                             "attached" on its own answers "was this dealt with?"
                             and not "where did it go?", and the second is the
                             question this queue exists for. -->
                        <ng-container *ngIf="payment.status === 'attached'">
                            <span class="p-text-secondary">attached</span>
                            <div *ngIf="payment.attachedJobCode" class="p-text-sm">
                                <a
                                    href="javascript:void(0)"
                                    (click)="store.dispatch(actions.attachedJobClicked({
                                        jobId: payment.attachedJobId
                                    }))"
                                >{{ payment.attachedJobCode }}</a>
                            </div>
                        </ng-container>

                        <!-- The reason a payment was dismissed is the only record
                             of why money was written off, so it belongs on the row
                             and not behind a hover. -->
                        <ng-container *ngIf="payment.status === 'dismissed'">
                            <span class="p-text-secondary">dismissed</span>
                            <div *ngIf="payment.dismissReason" class="p-text-secondary p-text-sm">
                                {{ payment.dismissReason }}
                            </div>
                        </ng-container>
                    </td>
                </tr>

                <!-- The resolve form, inline under the row it acts on, so nobody
                     can attach money to a job while looking at a different one. -->
                <tr *ngIf="vm.selectedId === payment.id">
                    <td colspan="7">
                        <div class="p-p-3 p-mb-2 surface-ground">
                            <div class="p-mb-3">
                                <!-- Ids derive from the payment so they stay unique: more
                                     than one row can be expanded at a time. -->
                                <label
                                    class="p-d-block p-mb-1"
                                    [for]="'bluesage-attach-' + payment.id"
                                >Attach to job</label>
                                <!-- A job CODE, which is what the terminal receipt and the
                                     row above both carry. It is pre-filled with the code the
                                     crew keyed, resolved to a job, and only the resolved id
                                     is ever sent. -->
                                <input
                                    pInputText
                                    [id]="'bluesage-attach-' + payment.id"
                                    class="p-mr-2"
                                    placeholder="Job code — e.g. HNLU-9PX-N57"
                                    [ngModel]="vm.jobCode"
                                    (ngModelChange)="store.dispatch(actions.jobCodeChanged({ jobCode: $event }))"
                                />
                                <button
                                    type="button"
                                    pButton
                                    label="Attach as payment"
                                    class="p-button-sm p-mr-2"
                                    pTooltip="Files it against the job's open invoice"
                                    [loading]="vm.resolvingId === payment.id"
                                    [disabled]="!vm.canAttachAsPayment"
                                    (click)="store.dispatch(actions.attachButtonClicked({
                                        paymentId: payment.id, fileAs: transactionType.Payment
                                    }))"
                                ></button>
                                <button
                                    type="button"
                                    pButton
                                    label="Attach as deposit"
                                    class="p-button-sm p-button-secondary"
                                    pTooltip="Only when the money really was taken as a deposit — it counts toward the job's deposit total"
                                    [loading]="vm.resolvingId === payment.id"
                                    [disabled]="!vm.canAttach"
                                    (click)="store.dispatch(actions.attachButtonClicked({
                                        paymentId: payment.id, fileAs: transactionType.Deposit
                                    }))"
                                ></button>

                                <!-- What the code resolved to. Shown BEFORE anything is
                                     clicked: this is the last point at which somebody can
                                     notice the money is about to land on the wrong job. -->
                                <div class="p-mt-2" *ngIf="vm.lookingUp">
                                    <span class="p-text-secondary p-text-sm">Looking up that code…</span>
                                </div>

                                <!-- A lookup that never ran must not look like one
                                     that ran and found nothing. Both used to render
                                     as an empty panel, so a code that genuinely
                                     exists came back silent and read as a broken
                                     search. -->
                                <div class="p-mt-2" *ngIf="!vm.lookingUp && vm.lookupFailed">
                                    <span class="p-error p-text-sm">
                                        Could not look that code up — this is NOT the same
                                        as no job having it. Try again.
                                    </span>
                                </div>

                                <div class="p-mt-2" *ngIf="!vm.lookingUp && vm.jobMatch as match">
                                    <div *ngIf="match.job as job">
                                        <strong>{{ job.code }}</strong>
                                        <span class="p-text-secondary">
                                            — {{ money(job.balance, job.currency) }} still owed
                                            of {{ money(job.total, job.currency) }}
                                        </span>
                                        <div
                                            class="p-text-sm p-mt-1"
                                            [class.p-text-secondary]="job.canTakePayment"
                                        >
                                            <span *ngIf="job.canTakePayment; else depositOnly">
                                                Has one open invoice and room for this amount —
                                                it can be attached as a payment.
                                            </span>
                                            <ng-template #depositOnly>
                                                No single open invoice with room for this amount,
                                                so it can only be attached as a
                                                <strong>deposit</strong>. Invoice the job first
                                                if this was really a payment.
                                            </ng-template>
                                        </div>

                                        <!-- The job's open authorizations. A device capture
                                             that lands here for a job holding one is very
                                             often that hold, completed on the terminal after
                                             the CRM stopped waiting (live, 2026-09-03).
                                             Completing the hold is the right action then —
                                             attaching would file a second payment and leave
                                             the hold open forever. -->
                                        <div class="p-mt-2" *ngIf="job.openHolds?.length">
                                            <div class="p-text-sm p-mb-1">
                                                <strong>This job has an open authorization.</strong>
                                                If this capture is that hold completed on the
                                                terminal, complete the hold instead of attaching:
                                            </div>
                                            <div
                                                *ngFor="let hold of job.openHolds"
                                                class="p-d-flex p-ai-center p-mb-1"
                                            >
                                                <span class="p-text-sm p-mr-2">
                                                    {{ money(hold.amount, job.currency) }} held
                                                    <span *ngIf="hold.epi">on {{ terminal(hold.epi) }}</span>
                                                    <span *ngIf="hold.createdAt" class="p-text-secondary">
                                                        · {{ when(hold.createdAt) | date: 'MMM d, h:mm a' }}
                                                    </span>
                                                    <span
                                                        *ngIf="hold.epi && payment.epi && hold.epi !== payment.epi"
                                                        class="p-error"
                                                    >
                                                        · different terminal from this capture
                                                    </span>
                                                    <span
                                                        *ngIf="hold.amount !== payment.baseAmount"
                                                        class="p-text-secondary"
                                                    >
                                                        · captured {{ money(payment.baseAmount, payment.currency) }}
                                                    </span>
                                                </span>
                                                <button
                                                    type="button"
                                                    pButton
                                                    label="Complete this hold"
                                                    class="p-button-sm"
                                                    pTooltip="Marks the hold paid with this capture's amounts and links the two"
                                                    [loading]="vm.resolvingId === payment.id && vm.resolvingHoldId === hold.transactionId"
                                                    [disabled]="!!vm.resolvingId"
                                                    (click)="store.dispatch(actions.completeHoldButtonClicked({
                                                        paymentId: payment.id, transactionId: hold.transactionId
                                                    }))"
                                                ></button>
                                            </div>
                                        </div>
                                    </div>

                                    <div *ngIf="!match.job" class="p-text-sm">
                                        {{ match.detail }}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label
                                    class="p-d-block p-mb-1"
                                    [for]="'bluesage-dismiss-' + payment.id"
                                >Or dismiss it</label>
                                <input
                                    pInputText
                                    [id]="'bluesage-dismiss-' + payment.id"
                                    class="p-mr-2"
                                    placeholder="Reason — this is the only record of why"
                                    [ngModel]="vm.dismissReason"
                                    (ngModelChange)="store.dispatch(actions.dismissReasonChanged({ dismissReason: $event }))"
                                />
                                <button
                                    type="button"
                                    pButton
                                    label="Dismiss"
                                    class="p-button-sm p-button-danger"
                                    [loading]="vm.resolvingId === payment.id"
                                    [disabled]="!vm.canDismiss"
                                    (click)="store.dispatch(actions.dismissButtonClicked({ paymentId: payment.id }))"
                                ></button>
                            </div>
                        </div>
                    </td>
                </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
                <tr>
                    <!-- "Empty" and "the load failed" must never look alike here.
                         This screen exists to show money nobody has claimed; a
                         reassuring empty state over a failed fetch is the one
                         wrong answer it can give. -->
                    <td colspan="7" *ngIf="!vm.loadFailed && !vm.loading" class="p-text-secondary">
                        Nothing here — every terminal payment has been attributed.
                    </td>
                    <td colspan="7" *ngIf="!vm.loadFailed && vm.loading" class="p-text-secondary">
                        Loading…
                    </td>
                    <td colspan="7" *ngIf="vm.loadFailed">
                        <span class="p-error">
                            Could not load the review queue — this is NOT the same as
                            there being nothing to review.
                        </span>
                        <button
                            type="button"
                            pButton
                            label="Try again"
                            class="p-button-sm p-button-text p-ml-2"
                            (click)="store.dispatch(actions.refreshButtonClicked())"
                        ></button>
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </div>
</ng-container>
